<template>
  <template v-if="!visible" />

  <pf-nav-item
    v-else-if="type === 'nav-item'"
    v-bind="{...ouiaProps, ...$attrs}"
    :class="{[buttonStyles.modifiers.disabled]: !granted }"
    :to="granted ? to : undefined"
    :title="title"
    :disabled="!granted"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <slot>{{ title }}</slot>
    {{ ' ' }}
    <form-help-tooltip v-if="!granted" :position="tooltipPosition" :text="disabledMessage" />
    <form-help-tooltip v-else-if="helpMessage" :position="tooltipPosition" :text="helpMessage" />
  </pf-nav-item>

  <router-link v-else-if="to" v-slot="{ navigate, href }" :to="granted ? to : {}" custom>
    <pf-dropdown-item
      v-if="type === 'dropdown-item'"
      v-bind="{...ouiaProps, ...$attrs}"
      :to="href"
      :title="title"
      :disabled="!granted"
      @click.prevent="granted ? navigate() : undefined"
      @keypress.enter.prevent="granted ? navigate() : undefined"
    >
      <template v-if="$slots.icon" #icon>
        <slot name="icon" />
      </template>
      <slot>{{ title }}</slot>
      {{ ' ' }}
      <form-help-tooltip v-if="!granted" :position="tooltipPosition" :text="disabledMessage" />
      <form-help-tooltip v-else-if="helpMessage" :position="tooltipPosition" :text="helpMessage" />
    </pf-dropdown-item>

    <pf-button
      v-else
      v-bind="{...ouiaProps, ...$attrs}"
      :variant="type === 'btn' ? 'control' : type"
      :inline="inline ?? (type === 'link')"
      :title="title"
      :href="href"
      :disabled="!granted"
      @click.prevent="granted ? navigate() : undefined"
      @keypress.enter.prevent="granted ? navigate() : undefined"
    >
      <template v-if="$slots.icon" #icon>
        <slot name="icon" />
      </template>
      <slot>{{ title }}</slot>
      {{ ' ' }}
      <form-help-tooltip v-if="!granted" :position="tooltipPosition" :text="disabledMessage" />
      <form-help-tooltip v-else-if="helpMessage" :position="tooltipPosition" :text="helpMessage" />
    </pf-button>
  </router-link>
</template>

<style lang="scss" scoped>
:deep(.pf-v6-c-nav__link) {
  &.pf-m-disabled {
    cursor: not-allowed;
    color: var(--pf-t--global--text--color--disabled);
  }
  &.pf-m-disabled:hover {
    background-color: transparent;
  }
}
</style>

<script lang="ts">
export interface Props extends /* @vue-ignore */ Omit<PfButtonProps, 'id' | 'variant' | 'tabindex' | 'to' | 'type' | 'href' | 'active' | 'inline'>, /* @vue-ignore */ Omit<PfMenuItemProps, 'id' | 'value' | 'to' | 'component' | 'download'> {
  to: RouteLocationRaw;
  forceDisabled?: boolean;
  disabledMessage?: string;
  helpMessage?: string;
  title?: string;
  type?: PfButtonProps['variant'] | 'btn' | 'nav-item' | 'dropdown-item';
  inline?: boolean;
  tooltipPosition?: PfTooltipProps['position'];
}
</script>

<script lang="ts" setup>
import { routeIsGranted, routeIsVisible } from '@/utils';
import { $t } from '@/i18n';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { computed, ref } from 'vue';
import { useChildrenTracker } from '@common/use';
import { useOUIAProps } from '@common/ouia';
import type { Props as PfButtonProps } from '@vue-patternfly/core/components/Button.vue';
import type { Props as PfMenuItemProps } from '@vue-patternfly/core/components/Menu/MenuItem.vue';
import type { Props as PfTooltipProps } from '@vue-patternfly/core/components/Tooltip/Tooltip.vue';
import { GrantedLinkKey } from './nav-group.vue';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import buttonStyles from '@patternfly/react-styles/css/components/Button/button';

defineOptions({
  inheritAttrs: false,
});

useChildrenTracker(GrantedLinkKey);

const props = withDefaults(defineProps<Props>(), {
  disabledMessage: $t('Non hai i permessi per visualizzare questa pagina. Richiedili ad un utente amministratore.'),
  type: 'link',
  inline: undefined,
});

const ouiaProps = useOUIAProps(props);

const router = useRouter();
const route = computed(() => router.resolve(props.to));
const granted = computed(() => routeIsGranted(route.value) && !props.forceDisabled);
const visible = computed(() => routeIsVisible(route.value));
const title = ref(props.title);
const active = computed(() => granted.value && router.currentRoute.value.name === route.value.name);

defineExpose({
  visible,
  active,
});
</script>
